import { FC, useEffect } from 'react';
import { Dropdown } from 'alurkerja-ui';
import { PanelLeft } from 'lucide-react';
import { TbBell } from 'react-icons/tb';
import useAuthState from '@/states/auth.state';
import { useUnifiedUserInfo, useUserAvatarUrl } from '@/utils/auth/userInfo';
import { getInitials } from '@/utils/functions/getInitials';
import clsx from 'clsx';
import { DarkModeToggle } from './DarkModeToggle';

export interface BellButtonProps {
  notificationCount?: number;
}

export interface HeaderProps extends BellButtonProps {
  showNotification?: boolean;
  onClickNotification?: () => void;
  onListenNotification?: (open: boolean) => void;
  avatarContent?: JSX.Element;
  notificationContent?: JSX.Element;
  toggled: boolean;
  setToggled: (toggled: boolean) => void;
  rightContent?: JSX.Element;
}

export const Header: FC<HeaderProps> = ({
  showNotification = true,
  onClickNotification,
  onListenNotification,
  avatarContent,
  notificationContent,
  notificationCount,
  toggled,
  setToggled,
  rightContent,
}) => {
  const { getToken } = useAuthState();
  const userInfo = useUnifiedUserInfo();
  const avatarUrl = useUserAvatarUrl();

  const buildNotification = (): JSX.Element => {
    if (!showNotification) {
      return <></>;
    }

    if (!onClickNotification) {
      return (
        <Dropdown
          triggerElement={<BellButton {...{ notificationCount }} />}
          content={notificationContent}
          onListen={onListenNotification}
        />
      );
    }

    return (
      <button onClick={onClickNotification} className="hover:cursor-pointer" aria-label="Open notifications">
        <BellButton {...{ notificationCount }} />
      </button>
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      getToken()
        .then(token => {
          if (!token) {
            console.warn('Token is invalid or expired');
          }
        })
        .catch(error => console.warn('Token check failed:', error));
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [getToken]);

  return (
    // <header className="flex z-20 items-center justify-between w-full h-16 md:px-8 px-6 border-b bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-white">
    //   <div className="flex items-center h-full gap-3 pl-2">
    //     <img src="/new-logo-aosonline.png" alt="AOS Online" className="h-9 w-auto object-contain block" />
    //     <button
    //       onClick={() => setToggled(!toggled)}
    //       className={clsx('transition-all duration-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 md:hidden rounded-md')}
    //       aria-label="Toggle sidebar"
    //     >
    //       <div className={clsx('transition-transform duration-300', toggled && 'rotate-180')}>
    //         <PanelLeft size={18} />
    //       </div>
    //     </button>
    //   </div>
    <header className="flex z-20 items-center justify-between w-full h-16 md:px-8 px-6 border-b bg-white dark:bg-gray-900 dark:border-gray-700 dark:text-white">
      <div className="flex items-center h-full gap-3 pl-4">
        <img src="/new-logo-aosonline.png" alt="AOS Online" className="h-12 w-auto object-contain block dark:hidden" />
        <img
          src="/new-logodark-aosonline.png"
          alt="AOS Online"
          className="h-12 w-auto object-contain hidden dark:block brightness-0 invert"
        />

        <button
          onClick={() => setToggled(!toggled)}
          className={clsx(
            'transition-all duration-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 md:hidden rounded-md',
          )}
          aria-label="Toggle sidebar"
        >
          <div className={clsx('transition-transform duration-300', toggled && 'rotate-180')}>
            <PanelLeft size={18} />
          </div>
        </button>
      </div>

      <div className="flex items-center gap-6 h-full">
        {rightContent && rightContent}

        {/* Notification + Avatar grouped together */}
        <div className="flex items-center gap-2">
          <DarkModeToggle />
          {buildNotification()}

          <Dropdown
            triggerElement={
              <div className="flex items-center gap-2">
                <div className="bg-gray-200 text-primary h-7 w-7 aspect-square cursor-pointer flex items-center justify-center rounded-full overflow-hidden">
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={`${userInfo?.name}'s avatar`}
                      className="w-full h-full aspect-square object-cover"
                    />
                  ) : (
                    userInfo?.name && <span className="text-[10px] font-semibold">{getInitials(userInfo.name)}</span>
                  )}
                </div>
                <div className="cursor-pointer select-none hidden md:block">
                  <span className="select-none font-medium text-sm text-gray-500">{userInfo?.name}</span>
                </div>
              </div>
            }
            content={avatarContent}
          />
        </div>
      </div>
    </header>
  );
};

const BellButton: FC<BellButtonProps> = ({ notificationCount }) => {
  const count = notificationCount ?? 0;
  const rightClass = count >= 100 ? '-right-4' : count >= 10 ? '-right-2' : 'right-0';

  return (
    <div className="relative p-1 hover:cursor-pointer hover:bg-black/5 rounded-md">
      {count > 0 && (
        <div
          className={`absolute top-0 py-[0.2px] px-[4px] text-[10px] text-white bg-red-500 rounded-full ${rightClass}`}
        >
          {count >= 100 ? '99+' : count}
        </div>
      )}

      <TbBell size={18} className="-rotate-12" />
    </div>
  );
};

export default Header;
